// Shopify embedded-surface bootstrap hook.
//
// TRD: docs/product/features/completed/shopify-embedded-install-auth/trd.md
// Bounded Unit B, B1 (conditional App Bridge init) + the session-exchange
// call B5/B6 depend on for project id / isShopifyAccountOwner.
//
// Deliberately separate from App.js's existing initUser()/validateAuth()
// cookie-session flow (TRD §0.3, A2): the embedded surface is authenticated
// per-request via a Bearer session token fetched fresh from App Bridge, not
// via httpOnly cookies -- there is no equivalent of `/user/profile` to call
// here. This hook is the ONE place that performs the one-time
// `POST /auth/shopify/session-exchange` handshake using the URL's raw
// `id_token` (only valid on the very first iframe load), then hands back a
// minimal { user, currentProject } stub App.js writes into its existing
// Redux/useState slots so the rest of the app's project-scoped routing
// (ProjectRouteGuard, AppLayout's isLoggedIn check, etc.) works
// unmodified -- deliberately reusing existing infra rather than forking a
// second render tree for the embedded surface.
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Config from '../Config';
import {
  establishShopifyEmbeddedContext,
  isShopifyEmbeddedContext,
  setShopifyProjectId,
  getShopifyProjectId,
  setShopifyAccountOwnerFromResponse,
} from './embeddedContext';
import { initAppBridge } from './appBridge';

/**
 * @returns {{
 *   isShopifyEmbedded: boolean,
 *   bootstrapping: boolean,
 *   projectId: string|null,
 *   isNewInstall: boolean,
 *   error: string|null,
 * }}
 */
export function useShopifyEmbeddedBootstrap({ initialSearch }) {
  const [state, setState] = useState(() => ({
    // Cheap synchronous pre-check so App.js can skip the standalone
    // profileLoading spinner branch correctly on the very first render,
    // before this effect has had a chance to run.
    isShopifyEmbedded: isShopifyEmbeddedContext() || !!parseInitialEmbedded(initialSearch),
    bootstrapping: true,
    projectId: getShopifyProjectId(),
    isNewInstall: false,
    error: null,
  }));
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const parsed = establishShopifyEmbeddedContext(initialSearch);

    if (!parsed && !isShopifyEmbeddedContext()) {
      // Not embedded, never was in this tab -- standalone page load.
      // Acceptance criteria (B1): App Bridge never invoked, no network
      // calls toward Shopify.
      setState(s => ({ ...s, isShopifyEmbedded: false, bootstrapping: false }));
      return;
    }

    initAppBridge();

    // A project id already persisted (e.g. a client-side route change
    // within the same tab re-ran this on remount) -- nothing left to do.
    const existingProjectId = getShopifyProjectId();
    if (existingProjectId) {
      setState(s => ({
        ...s,
        isShopifyEmbedded: true,
        bootstrapping: false,
        projectId: existingProjectId,
      }));
      return;
    }

    if (!parsed || !parsed.idToken) {
      // Embedded context established previously in this tab, but no
      // id_token on THIS load and no projectId persisted either --
      // shouldn't normally happen (id_token is only absent on a load that
      // isn't the initial Shopify-delivered one), but fail safely into a
      // visible in-iframe error rather than a silent hang.
      setState(s => ({
        ...s,
        isShopifyEmbedded: true,
        bootstrapping: false,
        error: 'Missing Shopify session token on initial load.',
      }));
      return;
    }

    // First-load session exchange (TRD A1). Uses the URL's raw id_token
    // directly as the Bearer token -- this is the one legitimate call that
    // does NOT go through getShopifySessionToken()/App Bridge, since Shopify
    // hands us this exact token as part of the initial iframe load itself
    // (App Bridge doesn't need to be asked for it). Deliberately a raw
    // axios call, not getCMResponse(path, method, ...): getCMResponse's
    // 401 handling assumes the JWT-cookie refresh flow (see api_manager.js)
    // which doesn't apply pre-session-exchange.
    axios.post(
      `${Config.BASE_URL}/auth/shopify/session-exchange`,
      {},
      { headers: { Authorization: `Bearer ${parsed.idToken}` } }
    ).then(response => {
      const data = response.data && response.data.data;
      const projectId = data && data.project_id;
      const isNewInstall = !!(data && data.is_new_install);
      if (!projectId) {
        throw new Error('session-exchange response missing project_id');
      }
      setShopifyProjectId(projectId);
      setShopifyAccountOwnerFromResponse(data);
      setState(s => ({
        ...s,
        isShopifyEmbedded: true,
        bootstrapping: false,
        projectId,
        isNewInstall,
      }));
    }).catch(err => {
      console.error('[shopify] session-exchange failed:', err);
      setState(s => ({
        ...s,
        isShopifyEmbedded: true,
        bootstrapping: false,
        error: 'Unable to establish a Shopify session. Please reload the app from your Shopify admin.',
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

function parseInitialEmbedded(search) {
  const params = new URLSearchParams(search);
  return params.get('embedded') === '1' && params.get('host') && params.get('shop');
}
